import React, { useEffect, useMemo, useState } from "react";

import { css, cx } from "@emotion/css";
import Input, { InputProps } from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { useTranslation } from "react-i18next";
import { z } from "zod/v4-mini";

import { useStyles } from "@/hooks/useStyles.ts";
import { BaseGlycemiaParameters, GlycemiaParametersType } from "@/models/DiabetesDataModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Schema } from "@/utils/zodUtils.ts";

export type ThresholdInputProps = {
  id?: string;
  diabetesParameters: BaseGlycemiaParameters;
  threshold: GlycemiaParametersType;
  onSubmit: (value: number) => void;
  className?: string;
  inputProps?: InputProps;
};

export const ThresholdInput: React.FC<ThresholdInputProps> = ({
  id,
  diabetesParameters,
  threshold,
  onSubmit,
  className,
  inputProps,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>(diabetesParameters[threshold].toString());

  const [parsedValue, error] = useMemo(() => {
    const data = thresholdValueSchemas(diabetesParameters)[threshold].safeParse(value);
    if (data.success) {
      return [data.data, false];
    } else {
      const parsedValue = value ? parseFloat(value) : 0;
      if (!Number.isNaN(parsedValue)) {
        return [parsedValue, true];
      }
    }
    return [0, true];
  }, [value, diabetesParameters, threshold]);

  const styles = useStyles(makeStyles, parsedValue, error);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (!error && parsedValue !== diabetesParameters[threshold]) {
        onSubmit(parsedValue);
      }
    }, 1000);

    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [error, parsedValue, onSubmit, diabetesParameters, threshold]);

  return (
    <Input
      id={id}
      onChange={event => setValue(event.target.value)}
      type="text"
      error={error}
      classes={{ root: cx(styles.root, className), input: styles.input }}
      disableUnderline
      value={value}
      endAdornment={
        <InputAdornment position="end" className={styles.adornment}>
          {t("thresholdSettings.units")}
        </InputAdornment>
      }
      {...inputProps}
    />
  );
};

const thresholdValueSchemas = (
  diabetesParameters: BaseGlycemiaParameters,
): Record<GlycemiaParametersType, Schema<number>> => ({
  severeHyperglycemia: z.coerce.number().check(z.minimum(diabetesParameters.hyperglycemia + 1)),
  hyperglycemia: z.coerce
    .number()
    .check(z.minimum(diabetesParameters.hypoglycemia + 1), z.maximum(diabetesParameters.severeHyperglycemia - 1)),
  hypoglycemia: z.coerce
    .number()
    .check(z.minimum(diabetesParameters.severeHypoglycemia + 1), z.maximum(diabetesParameters.hyperglycemia - 1)),
  severeHypoglycemia: z.coerce.number().check(z.maximum(diabetesParameters.hypoglycemia - 1)),
});

const makeStyles = (theme: Theme, value: number, error: boolean) => ({
  root: css``,
  input: css`
    border-bottom: ${error ? theme.palette.error.main : theme.palette.text.primary} ${theme.spacing(error ? 1 : 0.5)}
      solid;
    color: ${error ? theme.palette.error.main : theme.palette.text.primary};
    padding: 0;
    text-align: end;
    width: ${value.toString().length + 0.1}ch;
  `,
  adornment: css`
    .MuiTypography-root {
      color: ${error ? theme.palette.error.main : theme.palette.text.primary};
    }
  `,
});
